import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, FileSpreadsheet, FileText, AlertTriangle, CheckCircle2, Plus, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";

const LOC_LABEL = { warehouse: "Eigen", leverancier: "Leverancier", channeldock: "ChannelDock" };

export default function VoorraadAuditDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [alleenAfwijkend, setAlleenAfwijkend] = useState(false);
  const [zoek, setZoek] = useState("");
  const [boek, setBoek] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/voorraad-audits/${id}`); setD(data); }
    catch { toast.error("Audit niet gevonden"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const zetRegel = async (sku, veld, waarde) => {
    try {
      await api.put(`/voorraad-audits/${id}/regel`, { sku, [veld]: waarde === "" ? null : Number(waarde) });
      load();
    } catch { toast.error("Opslaan mislukt"); }
  };

  const download = (soort) => {
    const ext = soort === "excel" ? "xlsx" : "pdf";
    downloadFile(`/voorraad-audits/${id}/${soort}`, `voorraadaudit.${ext}`);
  };

  const regels = useMemo(() => {
    const q = zoek.trim().toLowerCase();
    return (d?.regels || []).filter(r =>
      (!alleenAfwijkend || !r.sluit_aan) &&
      (!q || `${r.sku} ${r.naam} ${r.ean}`.toLowerCase().includes(q)));
  }, [d, alleenAfwijkend, zoek]);

  if (!d) return <div className="p-6 text-muted-foreground">Laden…</div>;
  const a = d.audit, t = d.totalen;
  const locaties = a.locaties || [];
  const redenen = d.verlies_redenen || [];

  return (
    <div className="space-y-6" data-testid="voorraadaudit-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/voorraadaudit" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" />Alle audits
          </Link>
          <h1 className="font-display text-3xl mt-1">{a.naam}</h1>
          <p className="text-sm text-muted-foreground">
            {a.start_datum} t/m {a.eind_datum} · {(a.merken || []).join(", ") || "alle merken"} ·
            {" "}{locaties.map(l => LOC_LABEL[l] || l).join(", ")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => download("excel")} data-testid="audit-excel">
            <FileSpreadsheet className="h-4 w-4 mr-2" />Excel
          </Button>
          <Button variant="outline" onClick={() => download("pdf")} data-testid="audit-pdf">
            <FileText className="h-4 w-4 mr-2" />PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="audit-totalen">
        {[["Producten", t.producten], ["Beginvoorraad", t.begin], ["Inkoop", t.inkoop], ["Verkoop", t.verkoop],
          ["Verlies", t.verlies], ["Samples", t.sample], ["Berekend eind", t.berekend],
          ["Afwijkend", t.afwijkend]].map(([k, v]) => (
          <div key={k} className="bg-card border border-border rounded-xl p-4">
            <div className="text-xs text-muted-foreground">{k}</div>
            <div className={`font-display text-xl ${k === "Afwijkend" && v > 0 ? "text-destructive" : ""}`}>{v}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="overzicht">
        <TabsList>
          <TabsTrigger value="overzicht" data-testid="tab-overzicht">Voorraadverloop</TabsTrigger>
          <TabsTrigger value="verlies" data-testid="tab-verlies">Verloren producten</TabsTrigger>
          <TabsTrigger value="samples" data-testid="tab-samples">Samples</TabsTrigger>
        </TabsList>

        <TabsContent value="overzicht" className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Zoek op SKU, naam of EAN…"
              className="w-64" data-testid="audit-zoek" />
            <Button size="sm" variant={alleenAfwijkend ? "default" : "outline"}
              onClick={() => setAlleenAfwijkend(v => !v)} data-testid="audit-filter-afwijkend">
              Alleen afwijkingen ({t.afwijkend})
            </Button>
            <Button size="sm" variant="outline" data-testid="audit-begin-voorstel"
              onClick={async () => {
                if (!window.confirm("Beginvoorraad voorvullen met de systeemstand op de peildatum? Bestaande waarden blijven staan.")) return;
                try {
                  const { data } = await api.post(`/voorraad-audits/${id}/begin-voorstel`);
                  toast.success(`${data.gevuld} van ${data.producten} producten voorgevuld`);
                  load();
                } catch { toast.error("Voorvullen mislukt"); }
              }}>
              Beginvoorraad voorstellen
            </Button>
          </div>
          <div className="bg-card border border-border rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-xs uppercase tracking-wide">
                <tr>
                  <th className="p-2 text-left">SKU</th>
                  <th className="p-2 text-left">Product</th>
                  <th className="p-2 text-right">Begin</th>
                  <th className="p-2 text-right">Inkoop</th>
                  <th className="p-2 text-right">Verkoop</th>
                  <th className="p-2 text-right">Corr.</th>
                  {redenen.map(r => <th key={r} className="p-2 text-right whitespace-nowrap">{r}</th>)}
                  <th className="p-2 text-right">Verlies tot.</th>
                  <th className="p-2 text-right">Samples</th>
                  <th className="p-2 text-right">Berekend</th>
                  {locaties.map(l => <th key={l} className="p-2 text-right">{LOC_LABEL[l] || l}</th>)}
                  <th className="p-2 text-right">Systeem</th>
                  <th className="p-2 text-right">Geteld</th>
                  <th className="p-2 text-right">Verschil</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {regels.map(r => (
                  <tr key={r.sku} data-testid={`audit-regel-${r.sku}`}>
                    <td className="p-2 font-mono text-xs">{r.sku}</td>
                    <td className="p-2 max-w-[260px] truncate" title={r.naam}>{r.naam}</td>
                    <td className="p-2 text-right">
                      <Input defaultValue={r.begin} type="number" className="h-8 w-20 text-right"
                        onBlur={(e) => { if (Number(e.target.value) !== r.begin) zetRegel(r.sku, "begin", e.target.value || 0); }}
                        data-testid={`audit-begin-${r.sku}`} />
                    </td>
                    <td className="p-2 text-right text-muted-foreground">{r.inkoop}</td>
                    <td className="p-2 text-right text-muted-foreground">{r.verkoop}</td>
                    <td className="p-2 text-right text-muted-foreground">{r.correctie}</td>
                    {redenen.map(x => (
                      <td key={x} className="p-2 text-right text-muted-foreground" data-testid={`audit-verlies-${x}-${r.sku}`}>
                        {r.verlies_per_reden?.[x] || 0}
                      </td>
                    ))}
                    <td className="p-2 text-right text-muted-foreground">{r.verlies}</td>
                    <td className="p-2 text-right text-muted-foreground" data-testid={`audit-sample-${r.sku}`}>{r.sample}</td>
                    <td className="p-2 text-right font-medium">{r.berekend_eind}</td>
                    {locaties.map(l => <td key={l} className="p-2 text-right text-muted-foreground">{r.per_locatie?.[l] ?? 0}</td>)}
                    <td className="p-2 text-right font-medium">{r.systeem_eind}</td>
                    <td className="p-2 text-right">
                      <Input defaultValue={r.geteld ?? ""} type="number" placeholder="—" className="h-8 w-20 text-right"
                        onBlur={(e) => { const v = e.target.value; if (Number(v) !== r.geteld) zetRegel(r.sku, "geteld", v); }}
                        data-testid={`audit-geteld-${r.sku}`} />
                    </td>
                    <td className={`p-2 text-right font-medium ${r.sluit_aan ? "text-emerald-600" : "text-destructive"}`}>
                      {r.sluit_aan ? <CheckCircle2 className="h-4 w-4 inline" /> : <><AlertTriangle className="h-3 w-3 inline mr-1" />{r.verschil}</>}
                    </td>
                    <td className="p-2 text-right">
                      {!r.sluit_aan && (
                        <Button size="sm" variant="ghost" onClick={() => setBoek(r)} data-testid={`audit-boek-${r.sku}`}>
                          <Wand2 className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {regels.length === 0 && (
                  <tr><td colSpan={14 + locaties.length + redenen.length} className="p-6 text-center text-muted-foreground">
                    Geen producten in deze selectie.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          {locaties.includes("channeldock") && (
            <p className="text-xs text-muted-foreground">
              ChannelDock houdt zijn eigen voorraadadministratie bij; die mutaties staan niet in dit CRM. Verschillen in
              die kolom verklaar je met het ChannelDock-rapport.
            </p>
          )}
        </TabsContent>

        <TabsContent value="verlies">
          <VerliesTab auditId={id} van={a.start_datum} tot={a.eind_datum} onChange={load} />
        </TabsContent>

        <TabsContent value="samples">
          <SamplesTab auditId={id} van={a.start_datum} tot={a.eind_datum} onChange={load} />
        </TabsContent>
      </Tabs>

      <BoekVerschilDialog regel={boek} auditId={id} onClose={() => setBoek(null)} onDone={() => { setBoek(null); load(); }} />
    </div>
  );
}

function BoekVerschilDialog({ regel, auditId, onClose, onDone }) {
  const [f, setF] = useState({ doel: "systeem", als: "verlies", reden: "Vermist", notitie: "", organisatie_id: "" });
  if (!regel) return null;
  const submit = async () => {
    if (f.als === "sample" && !f.organisatie_id) return toast.error("Kies de organisatie die het sample kreeg");
    try {
      const { data } = await api.post(`/voorraad-audits/${auditId}/boek-verschil`, { sku: regel.sku, ...f });
      toast.success(`Verschil geboekt (${data.delta})`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Boeken mislukt"); }
  };
  return (
    <Dialog open={!!regel} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="audit-boek-dialog">
        <DialogHeader><DialogTitle>Verschil boeken — {regel.sku}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-secondary/50 rounded-md p-3 text-xs space-y-1">
            <div>Berekend eind: <b>{regel.berekend_eind}</b> · Systeem: <b>{regel.systeem_eind}</b>
              {regel.geteld !== null && <> · Geteld: <b>{regel.geteld}</b></>}</div>
            <div>Verschil: <b>{regel.verschil}</b></div>
          </div>
          <div>
            <Label>Wat wil je boeken?</Label>
            <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={f.doel} onChange={(e) => setF({ ...f, doel: e.target.value })} data-testid="boek-doel">
              <option value="systeem">Administratief verschil (berekend ↔ systeem), voorraad blijft gelijk</option>
              <option value="geteld">Voorraad bijstellen naar mijn telling</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Boeken als</Label>
              <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={f.als} onChange={(e) => setF({ ...f, als: e.target.value })} data-testid="boek-als">
                <option value="verlies">Verlies</option>
                <option value="sample">Sample (verkoopkosten)</option>
                <option value="correctie">Correctie</option>
              </select>
            </div>
            <div>
              <Label>Reden</Label>
              <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={f.reden} onChange={(e) => setF({ ...f, reden: e.target.value })} data-testid="boek-reden"
                disabled={f.als !== "verlies"}>
                {["Breuk", "Vermist", "Verlopen (THT)", "Weggegooid", "Anders"].map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
          {f.als === "sample" && (
            <OrganisatieKiezer waarde={f.organisatie_id} onKies={(id) => setF({ ...f, organisatie_id: id })}
              testId="boek-organisatie" />
          )}
          <div><Label>Notitie</Label>
            <Textarea value={f.notitie} onChange={(e) => setF({ ...f, notitie: e.target.value })} data-testid="boek-notitie" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuleren</Button>
          <Button onClick={submit} data-testid="boek-verstuur">Boeken</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VerliesTab({ auditId, van, tot, onChange }) {
  const [data, setData] = useState(null);
  const [producten, setProducten] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ product_id: "", aantal: "", datum: tot, reden: "Breuk", notitie: "", voorraad_deel: "warehouse" });

  const load = async () => {
    const [{ data: v }, { data: p }] = await Promise.all([
      api.get("/voorraad-verliezen", { params: { van, tot } }),
      producten.length ? Promise.resolve({ data: producten }) : api.get("/producten"),
    ]);
    setData(v);
    setProducten(Array.isArray(p) ? p : p.items || []);
  };
  useEffect(() => { load().catch(() => toast.error("Laden mislukt")); /* eslint-disable-next-line */ }, [van, tot]);

  const bewaar = async () => {
    if (!f.product_id || !Number(f.aantal)) return toast.error("Kies een product en een aantal");
    try {
      await api.post("/voorraad-verliezen", { ...f, aantal: Number(f.aantal), audit_id: auditId });
      toast.success("Verlies geboekt");
      setOpen(false); setF({ ...f, product_id: "", aantal: "", notitie: "" });
      await load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Boeken mislukt"); }
  };

  const verwijder = async (mid) => {
    if (!window.confirm("Verliesboeking terugdraaien?")) return;
    await api.delete(`/voorraad-verliezen/${mid}`);
    await load(); onChange?.();
  };

  if (!data) return <div className="p-6 text-muted-foreground">Laden…</div>;

  return (
    <div className="space-y-4" data-testid="verlies-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-3">
          <div className="bg-card border border-border rounded-xl px-4 py-3">
            <div className="text-xs text-muted-foreground">Totaal verloren ({van} t/m {tot})</div>
            <div className="font-display text-xl" data-testid="verlies-totaal">{data.totaal_stuks} stuks</div>
          </div>
          {(data.per_reden || []).slice(0, 4).map(r => (
            <div key={r.reden} className="bg-card border border-border rounded-xl px-4 py-3">
              <div className="text-xs text-muted-foreground">{r.reden}</div>
              <div className="font-medium text-lg">{r.stuks}</div>
            </div>
          ))}
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="verlies-nieuw-btn"><Plus className="h-4 w-4 mr-2" />Verlies boeken</Button>
          </DialogTrigger>
          <DialogContent data-testid="verlies-dialog">
            <DialogHeader><DialogTitle>Verloren product boeken</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Product</Label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={f.product_id} onChange={(e) => setF({ ...f, product_id: e.target.value })} data-testid="verlies-product">
                  <option value="">Kies een product…</option>
                  {producten.map(p => <option key={p.id} value={p.id}>{p.sku} — {p.naam}</option>)}
                </select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Aantal stuks</Label>
                  <Input type="number" value={f.aantal} onChange={(e) => setF({ ...f, aantal: e.target.value })}
                    data-testid="verlies-aantal" /></div>
                <div><Label>Datum</Label>
                  <Input type="date" value={f.datum} onChange={(e) => setF({ ...f, datum: e.target.value })}
                    data-testid="verlies-datum" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Reden</Label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                    value={f.reden} onChange={(e) => setF({ ...f, reden: e.target.value })} data-testid="verlies-reden">
                    {(data.redenen || []).map(r => <option key={r}>{r}</option>)}
                  </select></div>
                <div><Label>Locatie</Label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                    value={f.voorraad_deel} onChange={(e) => setF({ ...f, voorraad_deel: e.target.value })} data-testid="verlies-locatie">
                    <option value="warehouse">Eigen warehouse</option>
                    <option value="leverancier">Leverancier</option>
                  </select></div>
              </div>
              <div><Label>Notitie</Label>
                <Textarea value={f.notitie} onChange={(e) => setF({ ...f, notitie: e.target.value })} data-testid="verlies-notitie" /></div>
              <p className="text-xs text-muted-foreground bg-secondary/50 rounded-md p-3">
                De voorraad wordt direct met dit aantal verlaagd en de boeking komt in het auditrapport.
              </p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
              <Button onClick={bewaar} data-testid="verlies-opslaan">Boeken</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wide">
            <tr>
              <th className="p-2 text-left">Datum</th>
              <th className="p-2 text-left">SKU</th>
              <th className="p-2 text-left">Product</th>
              <th className="p-2 text-right">Stuks</th>
              <th className="p-2 text-left">Reden</th>
              <th className="p-2 text-left">Locatie</th>
              <th className="p-2 text-left">Notitie</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.items.map(r => (
              <tr key={r.id} data-testid={`verlies-rij-${r.id}`}>
                <td className="p-2">{r.datum?.slice(0, 10)}</td>
                <td className="p-2 font-mono text-xs">{r.sku}</td>
                <td className="p-2 max-w-[280px] truncate" title={r.product}>{r.product}</td>
                <td className="p-2 text-right font-medium">{r.stuks}</td>
                <td className="p-2"><Badge variant="outline">{r.reden || "Anders"}</Badge></td>
                <td className="p-2 text-muted-foreground">{LOC_LABEL[r.voorraad_deel] || r.voorraad_deel}</td>
                <td className="p-2 text-muted-foreground max-w-[240px] truncate" title={r.notitie}>{r.notitie}</td>
                <td className="p-2 text-right">
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => verwijder(r.id)}
                    data-testid={`verlies-verwijder-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Geen verliesboekingen in deze periode.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Zoek-en-kies-veld voor organisaties (verplicht bij een sample-registratie).
function OrganisatieKiezer({ waarde, onKies, testId = "organisatie-kiezer" }) {
  const [q, setQ] = useState("");
  const [opties, setOpties] = useState([]);
  const [gekozen, setGekozen] = useState(null);

  useEffect(() => {
    if (!q.trim() || gekozen) { setOpties([]); return; }
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/organisaties", { params: { q: q.trim(), limit: 8 } });
        setOpties((Array.isArray(data) ? data : data.items || []).slice(0, 8));
      } catch { setOpties([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [q, gekozen]);

  useEffect(() => { if (!waarde) { setGekozen(null); setQ(""); } }, [waarde]);

  return (
    <div>
      <Label>Aan wie gegeven (organisatie) <span className="text-destructive">*</span></Label>
      {gekozen ? (
        <div className="flex items-center justify-between gap-2 border border-border rounded-md px-3 py-2 text-sm"
          data-testid={`${testId}-gekozen`}>
          <span className="truncate">{gekozen.naam}{gekozen.plaats ? ` · ${gekozen.plaats}` : ""}</span>
          <Button size="sm" variant="ghost" onClick={() => { setGekozen(null); onKies(""); }}
            data-testid={`${testId}-wissen`}>Wijzigen</Button>
        </div>
      ) : (
        <>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op naam, klantcode of plaats…"
            data-testid={testId} autoComplete="off" />
          {opties.length > 0 && (
            <div className="mt-1 border border-border rounded-md divide-y divide-border max-h-44 overflow-y-auto">
              {opties.map(o => (
                <button key={o.id} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-secondary"
                  onClick={() => { setGekozen(o); onKies(o.id); setOpties([]); }}
                  data-testid={`${testId}-optie-${o.id}`}>
                  <div className="truncate">{o.naam}</div>
                  <div className="text-xs text-muted-foreground">{[o.klantcode, o.plaats].filter(Boolean).join(" · ")}</div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SamplesTab({ auditId, van, tot, onChange }) {
  const [data, setData] = useState(null);
  const [producten, setProducten] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ product_id: "", aantal: "", datum: tot, organisatie_id: "", notitie: "", voorraad_deel: "warehouse" });

  const load = async () => {
    const [{ data: s }, { data: p }] = await Promise.all([
      api.get("/voorraad-samples", { params: { van, tot } }),
      producten.length ? Promise.resolve({ data: producten }) : api.get("/producten"),
    ]);
    setData(s);
    setProducten(Array.isArray(p) ? p : p.items || []);
  };
  useEffect(() => { load().catch(() => toast.error("Laden mislukt")); /* eslint-disable-next-line */ }, [van, tot]);

  const bewaar = async () => {
    if (!f.product_id || !Number(f.aantal)) return toast.error("Kies een product en een aantal");
    if (!f.organisatie_id) return toast.error("Kies de organisatie die het sample kreeg");
    try {
      await api.post("/voorraad-samples", { ...f, aantal: Number(f.aantal), audit_id: auditId });
      toast.success("Sample geregistreerd");
      setOpen(false);
      setF({ ...f, product_id: "", aantal: "", notitie: "", organisatie_id: "" });
      await load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Registreren mislukt"); }
  };

  const verwijder = async (mid) => {
    if (!window.confirm("Sample-registratie terugdraaien?")) return;
    await api.delete(`/voorraad-samples/${mid}`);
    await load(); onChange?.();
  };

  if (!data) return <div className="p-6 text-muted-foreground">Laden…</div>;

  return (
    <div className="space-y-4" data-testid="samples-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-3">
          <div className="bg-card border border-border rounded-xl px-4 py-3">
            <div className="text-xs text-muted-foreground">Samples weggegeven ({van} t/m {tot})</div>
            <div className="font-display text-xl" data-testid="samples-totaal">{data.totaal_stuks} stuks</div>
            <div className="text-xs text-muted-foreground mt-0.5">geboekt als {data.kostenpost}</div>
          </div>
          {(data.per_organisatie || []).slice(0, 3).map(r => (
            <div key={r.organisatie} className="bg-card border border-border rounded-xl px-4 py-3">
              <div className="text-xs text-muted-foreground max-w-[180px] truncate" title={r.organisatie}>{r.organisatie}</div>
              <div className="font-medium text-lg">{r.stuks}</div>
            </div>
          ))}
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="sample-nieuw-btn"><Plus className="h-4 w-4 mr-2" />Sample registreren</Button>
          </DialogTrigger>
          <DialogContent data-testid="sample-dialog">
            <DialogHeader><DialogTitle>Sample registreren</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Product</Label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={f.product_id} onChange={(e) => setF({ ...f, product_id: e.target.value })} data-testid="sample-product">
                  <option value="">Kies een product…</option>
                  {producten.map(p => <option key={p.id} value={p.id}>{p.sku} — {p.naam}</option>)}
                </select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Aantal stuks</Label>
                  <Input type="number" value={f.aantal} onChange={(e) => setF({ ...f, aantal: e.target.value })}
                    data-testid="sample-aantal" /></div>
                <div><Label>Datum</Label>
                  <Input type="date" value={f.datum} onChange={(e) => setF({ ...f, datum: e.target.value })}
                    data-testid="sample-datum" /></div>
              </div>
              <OrganisatieKiezer waarde={f.organisatie_id} onKies={(id) => setF({ ...f, organisatie_id: id })}
                testId="sample-organisatie" />
              <div><Label>Locatie</Label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={f.voorraad_deel} onChange={(e) => setF({ ...f, voorraad_deel: e.target.value })} data-testid="sample-locatie">
                  <option value="warehouse">Eigen warehouse</option>
                  <option value="leverancier">Leverancier</option>
                </select></div>
              <div><Label>Notitie</Label>
                <Textarea value={f.notitie} onChange={(e) => setF({ ...f, notitie: e.target.value })} data-testid="sample-notitie" /></div>
              <p className="text-xs text-muted-foreground bg-secondary/50 rounded-md p-3">
                De voorraad wordt direct verlaagd. Samples tellen in de audit als {data.kostenpost} — dus níet als verlies.
              </p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
              <Button onClick={bewaar} data-testid="sample-opslaan">Registreren</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wide">
            <tr>
              <th className="p-2 text-left">Datum</th>
              <th className="p-2 text-left">SKU</th>
              <th className="p-2 text-left">Product</th>
              <th className="p-2 text-right">Stuks</th>
              <th className="p-2 text-left">Ontvanger</th>
              <th className="p-2 text-left">Locatie</th>
              <th className="p-2 text-left">Notitie</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.items.map(r => (
              <tr key={r.id} data-testid={`sample-rij-${r.id}`}>
                <td className="p-2">{r.datum?.slice(0, 10)}</td>
                <td className="p-2 font-mono text-xs">{r.sku}</td>
                <td className="p-2 max-w-[240px] truncate" title={r.product}>{r.product}</td>
                <td className="p-2 text-right font-medium">{r.stuks}</td>
                <td className="p-2 max-w-[220px] truncate" title={r.organisatie_naam}>
                  {r.organisatie_id
                    ? <Link to={`/organisaties/${r.organisatie_id}`} className="text-primary hover:underline">{r.organisatie_naam || "—"}</Link>
                    : <span className="text-muted-foreground italic">onbekend</span>}
                </td>
                <td className="p-2 text-muted-foreground">{LOC_LABEL[r.voorraad_deel] || r.voorraad_deel}</td>
                <td className="p-2 text-muted-foreground max-w-[220px] truncate" title={r.notitie}>{r.notitie}</td>
                <td className="p-2 text-right">
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => verwijder(r.id)}
                    data-testid={`sample-verwijder-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Nog geen samples geregistreerd in deze periode.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
